'use client'

import { motion, useReducedMotion } from 'motion/react'
import { cn } from '../../../../lib/cn'

export interface GridDiagonalCenterProps {
  /**
   * Enable entrance animation
   * @default true
   */
  animate?: boolean

  /**
   * Animation delay in seconds (allows hero content to load first)
   * @default 0.5
   */
  animationDelay?: number

  className?: string
}

/**
 * GridDiagonalCenter - Diagonal grid pattern with center fade
 *
 * 45-degree crossed lines creating a diamond/argyle pattern
 * that fades from the center outward.
 *
 * Fixed fadeSize: 0.45
 *
 * @example
 * ```tsx
 * <section className="relative">
 *   <GridDiagonalCenter />
 *   <div className="relative z-10">Content here</div>
 * </section>
 * ```
 */
export function GridDiagonalCenter({
  animate = true,
  animationDelay = 0.5,
  className,
}: GridDiagonalCenterProps = {}) {
  const prefersReducedMotion = useReducedMotion()
  const fadeSize = 0.45
  const size = `${fadeSize * 100}%`
  const maskGradient = `radial-gradient(ellipse ${size} ${size} at 50% 50%, #000 40%, transparent 100%)`

  return (
    <motion.div
      className={cn('absolute inset-0 z-0', className)}
      style={{
        backgroundImage: `
          repeating-linear-gradient(45deg, color-mix(in srgb, var(--border), transparent 60%) 0px, color-mix(in srgb, var(--border), transparent 60%) 1px, transparent 1px, transparent 20px),
          repeating-linear-gradient(-45deg, color-mix(in srgb, var(--border), transparent 60%) 0px, color-mix(in srgb, var(--border), transparent 60%) 1px, transparent 1px, transparent 20px)
        `,
        maskImage: maskGradient,
        WebkitMaskImage: maskGradient,
      }}
      initial={animate ? { opacity: 0 } : false}
      animate={animate ? { opacity: 1 } : false}
      transition={{
        duration: prefersReducedMotion ? 0.3 : 1.2,
        delay: animate ? (prefersReducedMotion ? 0 : animationDelay) : 0,
        ease: [0.16, 1, 0.3, 1],
      }}
    />
  )
}
